import type { ServiceFormData } from '@takeshape/service-providers';
import type { ImmutableMap } from 'app/common/utils/immutable-types';
import { createEntitlementValueSelector } from 'app/entitlements/selectors';
import { getProjectEntitlement } from 'app/project/selectors';
import type { ProjectSlice } from 'app/project/types';
import type { FormState, SchemaFormState } from 'app/schema-form/types';
import { createSelector, type Selector } from 'reselect';
import type { AppState } from '../app-state';
import { NAME, SERVICE_FORM_NAME } from './constants';
import type { ServiceState } from './reducer';

export const getServiceForm: Selector<AppState, FormState<ServiceFormData>> = (
  state: AppState
): FormState<ServiceFormData> => {
  const form = (state.schemaForm as unknown as ImmutableMap<SchemaFormState>).getIn(['forms', SERVICE_FORM_NAME]);
  return (form as ImmutableMap<FormState<ServiceFormData>>).toJS();
};

export const getServiceFormData = createSelector(getServiceForm, (form) => form.data);

export const getServiceTitle = createSelector(getServiceFormData, (form) => form.title);

export const getServiceShopifyShopUrl = createSelector(getServiceFormData, (form) => form.shop);

export const getServiceId = createSelector(getServiceFormData, (form) => form.id);

export const getServiceFormEndpoint = createSelector(getServiceFormData, (form) => form.endpoint);

export const getServiceFormAuthenticationType = createSelector(getServiceFormData, (form) => form?.authenticationType);

export const getServiceFormDirty = createSelector(getServiceForm, (form) => form.dirty ?? []);

export const getServiceFormSubmitted = createSelector(getServiceForm, (form) => form.submitted);

export const getServiceState = (state: AppState): ServiceState => state[NAME];

export const getServiceLoading = createSelector(getServiceState, (state) => state.loading);

export const getBootstrapDialogOpen = createSelector(getServiceState, (state) => state.bootstrapDialogOpen);

export const getBootstrapDialogProvider = createSelector(getServiceState, (state) => state.bootstrapDialogProvider);

export const getBootstrapDialogTypes = createSelector(getServiceState, (state) => state.bootstrapDialogTypes);

export const getShouldClearServiceHealthChecks = createSelector(
  getServiceState,
  (state) => state.shouldClearHealthChecks
);

export const getServiceHealthChecks = createSelector(getServiceState, (state) => state.healthChecks);

export const getServiceHealthCheck = createSelector(
  getServiceHealthChecks,
  (state: unknown, props: { serviceId?: string }): string | undefined => props.serviceId,
  (healthChecks, serviceId) => (serviceId ? healthChecks[serviceId] : undefined)
);

export const getInvalidServiceConfigurationDialogContent = createSelector(
  getServiceState,
  (state) => state.invalidConfigurationDialogContent
);

export const getProvidersEntitlement = (state: ProjectSlice): string[] =>
  (getProjectEntitlement(state, { entitlement: 'providers' }) as string[] | null) ?? [];

export const getExperimentalProvidersEntitlement = createEntitlementValueSelector('experimentalProviders');
